import type { AccessContext, AccessPermissionKey } from './contracts/accessModel.ts';
import { ACCESS_PERMISSIONS, normalizeAccessPermissions } from './contracts/accessModel.ts';
import type { MockRecord } from './database.ts';
import { db } from './helpers.ts';

export function accessProfileFor(user: MockRecord): AccessContext {
  if (user.accessProfile) {
    const profile = user.accessProfile as AccessContext;
    return { ...profile, permissions: normalizeAccessPermissions(profile.permissions),
      userId: String(user.userId ?? user.oid ?? profile.userId) };
  }
  const userId = String(user.userId ?? user.oid);
  const database = db();
  const permissionGrantModel = !!database.permissionGrantMigrations?.some((entry) => entry.oid === 'permission-grants-4');
  const roleOwned = user.roleScopeVersion === 3 || !!database.roleScopeMigrations?.some((entry) => entry.oid === 'role-owned-scope-3');
  const permissions = normalizeAccessPermissions(database.accessPermissions ?? ACCESS_PERMISSIONS.map((permission) => ({ oid: permission.permissionKey, ...permission })));
  return {
    userId,
    ...(permissionGrantModel ? { permissionGrantVersion: 4 as const, roleMemberships: (database.userRoleMemberships ?? [])
      .filter((row) => String(row.userId) === userId).map((row) => ({ assignmentId: String(row.oid), userId, roleId: String(row.roleId) })) }
      : roleOwned ? { roleScopeVersion: 3 as const, roleMemberships: (database.userRoleMemberships ?? [])
      .filter((row) => String(row.userId) === userId).map((row) => ({ assignmentId: String(row.oid), userId, roleId: String(row.roleId) })) } : {}),
    ...(!permissionGrantModel && (user.assignmentScopeVersion === 2 ? { assignmentScopeVersion: 2 as const }
      : Array.isArray(user.profilePslIds) ? { profilePslIds: user.profilePslIds.map(String) } : {})),
    assignments: (roleOwned ? [] : database.userRoleAssignments ?? []).filter((row) => String(row.userId) === userId).map((row) => ({
      assignmentId: String(row.oid), userId: String(row.userId), roleId: String(row.roleId), pslId: String(row.pslId),
    })),
    roles: (database.accessRoles ?? []).map((row) => ({
      roleId: String(row.oid), roleName: String(row.roleName), description: String(row.description ?? ''),
      isActive: row.isActive !== false && row.enabled !== false,
      ...(!permissionGrantModel && roleOwned ? { pslIds: Array.isArray(row.pslIds) ? row.pslIds.map(String) : [] } : {}),
    })),
    rolePermissions: (database.rolePermissions ?? []).map((row) => ({ roleId: String(row.roleId), permissionKey: String(row.permissionKey) as AccessPermissionKey })),
    rolePslPermissions: (database.rolePslPermissions ?? []).map((row) => ({
      roleId: String(row.roleId), pslId: String(row.pslId), permissionKey: String(row.permissionKey) as AccessPermissionKey,
      effect: row.effect === 'deny' ? 'deny' as const : 'allow' as const,
    })),
    ...(permissionGrantModel ? { rolePermissionGrants: (database.rolePermissionGrants ?? []).map((row) => ({
      roleId: String(row.roleId), pslId: String(row.pslId), permissionId: String(row.permissionId ?? row.permissionKey) as AccessPermissionKey,
    })) } : {}),
    permissions: permissions.map((row) => ({ permissionKey: String(row.permissionKey) as AccessPermissionKey,
      module: String(row.module), action: String(row.action), description: String(row.description),
      scopeRule: row.scopeRule as 'record' | 'any-psl' | 'global-only' })),
    psls: (database.psls ?? []).map((row) => ({ pslId: String(row.oid), name: String(row.name),
      isActive: row.isActive !== false && row.enabled !== false,
      isGlobal: row.isGlobal === true || String(row.name).trim().toLowerCase() === 'global' })),
  };
}